import { ENDPOINTS } from '@/settings/endpoints';
import axios, { AxiosError, AxiosInstance, AxiosRequestHeaders } from 'axios';

export interface CollaborationResponse<T> {
  data: T;
  message: string;
  identifier: string;
}

export enum EligibilityStatus {
  ELIGIBILITY_CREATED = 'ELIGIBILITY_CREATED',
  ELIGIBILITY_UPDATED = 'ELIGIBILITY_UPDATED',
  NO_MIN_GRADE = 'NO_MIN_GRADE',
  NO_MIN_PROGRESS = 'NO_MIN_PROGRESS',
  NO_MIN_GRADE_AND_PROGRESS = 'NO_MIN_GRADE_AND_PROGRESS',
  FAIL_UNKNOWN = 'FAIL_UNKNOWN',
}

export interface MicrocertificateEligibility {
  submoduleId: string;
  status: EligibilityStatus;
}

export interface MicrocertificateFilesUrl {
  pdfUrl: string;
  pngUrl: string;
  hash: string;
}

export interface UserMicrocertificateDTO {
  submoduleId?: string;
  disciplineId?: string;
  microcertificateId: string;
  microcertificateName: string;
  userMicrocertificateId: number | null;
  emissionDate: string | null;
  createdAt: string | null;
}

export class MicrocertificatesService {
  private gateway: AxiosInstance;

  constructor() {
    const dexToken = localStorage.getItem('token');

    this.gateway = axios.create({
      baseURL: ENDPOINTS.pegasusPepeLegal,
      headers: {
        'Authorization-Dex': `Bearer ${dexToken}`,
      },
    });
  }

  async setMicrocertificatesEligibility(courseId: string): Promise<MicrocertificateEligibility[]> {
    try {
      const { data: response } = await this.gateway.post<
        CollaborationResponse<MicrocertificateEligibility[]>
      >('/microcertificates/set-user-eligibility', {
        courseId,
      });

      return response.data;
    } catch (error: any) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.message || error.message;

        throw new Error(errorMessage);
      }

      throw error;
    }
  }

  async getMicrocertificateFiles(microcertificateId: string): Promise<MicrocertificateFilesUrl> {
    try {
      const { data: response } = await this.gateway.get<
        CollaborationResponse<MicrocertificateFilesUrl>
      >('/microcertificates/get-microcertificate-pdf', {
        params: {
          microcertificateId,
        },
        timeout: 10000,
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      return {
        ...response.data,
      };
    } catch (error: any) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.message || error.message;

        throw new Error(errorMessage);
      }

      throw error;
    }
  }

  async getUserMicrocertificates(
    submodulesIds: string[],
    disciplinesIds: string[],
    noCache: boolean = false,
  ): Promise<UserMicrocertificateDTO[]> {
    try {
      const headers: AxiosRequestHeaders = noCache ? { 'Cache-Control': 'no-cache' } : {};

      const { data: response } = await this.gateway.get<
        CollaborationResponse<UserMicrocertificateDTO[]>
      >(`/microcertificates/get-user-course-microcertificates`, {
        params: {
          submodulesIds,
          disciplineIds: disciplinesIds,
        },
        headers,
      });

      return response.data ?? [];
    } catch (error: any) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.message || error.message;

        throw new Error(errorMessage);
      }

      throw error;
    }
  }
}
